import { zodResolver } from '@hookform/resolvers/zod';
import { type CountryCode, parsePhoneNumberFromString } from 'libphonenumber-js';
import {
  Briefcase,
  Camera,
  Loader2,
  Mail,
  MapPin,
  Phone,
  Plus,
  Save,
  StickyNote,
  Tag as TagIcon,
  Trash,
  Trash2,
  User,
} from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import {
  type Control,
  Controller,
  type FieldArrayWithId,
  type FieldValues,
  type Resolver,
  type UseFormSetValue,
  useFieldArray,
  useForm,
  useWatch,
} from 'react-hook-form';
import { z } from 'zod';
import { TiptapEditor } from '../../components/comments/tiptap-editor';
import { FormRenderer } from '../../custom-fields/renderers/form-renderer';
import {
  extractFileAssociations,
  getCustomFieldDefaults,
  mapSchemaToFields,
  prepareContactSubmission,
} from '../../custom-fields/utils';
import { useFileUpload } from '../../hooks/useFileUpload';
import { formatAddress } from '../../lib/address';
import { sanitizeRichText } from '../../lib/rich-text';
import { type RouterOutputs, trpc, trpcClient } from '../../lib/trpc';
import { Button } from '../../ui/button';
import { Checkbox } from '../../ui/checkbox';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { UserAvatar } from '../../ui/user-avatar';
import { AddressEditor, type AddressEditorValue } from '../addresses/AddressEditor';
import { ImageCropperModal } from '../profile/ImageCropperModal';
import { useToast } from '../toast';
import { TagChips } from './TagChips';

// Radix Select forbids an empty-string item value, so the "no type" choice uses
// this sentinel and is mapped back to `null` (a genuinely typeless contact) on change.
const NO_TYPE_VALUE = 'none';

// Matches the limit ProfileForm advertises for user avatars.
const MAX_AVATAR_BYTES = 5 * 1024 * 1024;

const emailItemSchema = z.object({
  email: z.string().email('Invalid email address'),
  label: z.string().default('work'),
  isPrimary: z.boolean().default(false),
});

const phoneItemSchema = z.object({
  phone: z.string().min(1, 'Phone is required'),
  phoneRaw: z.string().optional(),
  label: z.string().default('work'),
  isPrimary: z.boolean().default(false),
});

export const contactEditSchema = z.object({
  label: z.string().min(1, 'Label is required'),
  content: z.string().nullable().optional(),
  metadata: z.string().nullable().optional(),
  // Type is optional — a typeless contact is valid and must remain saveable.
  typeId: z.number().nullable().optional(),
  displayName: z.string().min(1, 'Name is required'),
  avatarUrl: z.string().nullable().optional(),
  prefix: z.string().nullable().optional(),
  firstName: z.string().nullable().optional(),
  middleName: z.string().nullable().optional(),
  lastName: z.string().nullable().optional(),
  suffix: z.string().nullable().optional(),
  jobTitle: z.string().nullable().optional(),
  companyName: z.string().nullable().optional(),
  website: z.string().nullable().optional(),
  ownerId: z.number({ error: 'Owner is required' }),
  addressStreet: z.string().nullable().optional(),
  addressCity: z.string().nullable().optional(),
  addressState: z.string().nullable().optional(),
  addressPostalCode: z.string().nullable().optional(),
  addressCountry: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  emails: z.array(emailItemSchema).optional(),
  phones: z.array(phoneItemSchema).optional(),
  filterSlot1: z.union([z.string(), z.number()]).nullable().optional(),
  filterSlot2: z.union([z.string(), z.number()]).nullable().optional(),
  filterSlot3: z.union([z.string(), z.number()]).nullable().optional(),
  filterSlot4: z.union([z.string(), z.number()]).nullable().optional(),
  filterSlot5: z.union([z.string(), z.number()]).nullable().optional(),
  filterSlot6: z.union([z.string(), z.number()]).nullable().optional(),
  filterSlot7: z.union([z.string(), z.number()]).nullable().optional(),
  filterSlot8: z.union([z.string(), z.number()]).nullable().optional(),
  filterSlot9: z.union([z.string(), z.number()]).nullable().optional(),
  filterSlot10: z.union([z.string(), z.number()]).nullable().optional(),
  filterSlot11: z.union([z.string(), z.number()]).nullable().optional(),
  filterSlot12: z.union([z.string(), z.number()]).nullable().optional(),
  filterSlot13: z.union([z.string(), z.number()]).nullable().optional(),
  filterSlot14: z.union([z.string(), z.number()]).nullable().optional(),
  filterSlot15: z.union([z.string(), z.number()]).nullable().optional(),
  filterSlot16: z.union([z.string(), z.number()]).nullable().optional(),
  filterSlot17: z.union([z.string(), z.number()]).nullable().optional(),
  filterSlot18: z.union([z.string(), z.number()]).nullable().optional(),
  filterSlot19: z.union([z.string(), z.number()]).nullable().optional(),
  filterSlot20: z.union([z.string(), z.number()]).nullable().optional(),
  fileAssociations: z
    .array(
      z.object({
        fileId: z.number(),
        category: z.string(),
      })
    )
    .optional(),
});

export type ContactEditFormData = z.infer<typeof contactEditSchema>;

const countries = [
  { code: 'US' as CountryCode, name: 'United States', flag: '🇺🇸', dialCode: '+1' },
  { code: 'AU' as CountryCode, name: 'Australia', flag: '🇦🇺', dialCode: '+61' },
  { code: 'GB' as CountryCode, name: 'United Kingdom', flag: '🇬🇧', dialCode: '+44' },
  { code: 'CA' as CountryCode, name: 'Canada', flag: '🇨🇦', dialCode: '+1' },
  { code: 'NZ' as CountryCode, name: 'New Zealand', flag: '🇳🇿', dialCode: '+64' },
  { code: 'SG' as CountryCode, name: 'Singapore', flag: '🇸🇬', dialCode: '+65' },
  { code: 'DE' as CountryCode, name: 'Germany', flag: '🇩🇪', dialCode: '+49' },
  { code: 'FR' as CountryCode, name: 'France', flag: '🇫🇷', dialCode: '+33' },
  { code: 'IN' as CountryCode, name: 'India', flag: '🇮🇳', dialCode: '+91' },
];

interface PhoneInputRowProps {
  index: number;
  control: Control<ContactEditFormData>;
  setValue: UseFormSetValue<ContactEditFormData>;
  onRemove: () => void;
  phoneFields: FieldArrayWithId<ContactEditFormData, 'phones', 'id'>[];
}

function PhoneInputRow({ index, control, setValue, onRemove, phoneFields }: PhoneInputRowProps) {
  const initialValue = control._defaultValues?.phones?.[index]?.phone || '';
  const initialRaw = control._defaultValues?.phones?.[index]?.phoneRaw || '';

  const parsed = initialValue ? parsePhoneNumberFromString(initialValue) : null;
  const initCountry: CountryCode = parsed?.country || 'US';
  const initInput = parsed ? parsed.formatNational() : initialRaw || initialValue;

  const [country, setCountry] = useState<CountryCode>(initCountry);
  const [phoneInput, setPhoneInput] = useState(initInput);
  const [errorMsg, setErrorMsg] = useState('');

  const validateAndFormat = (inputVal: string, cCode: CountryCode) => {
    if (!inputVal.trim()) {
      setValue(`phones.${index}.phone`, '');
      setValue(`phones.${index}.phoneRaw`, '');
      setErrorMsg('');
      return;
    }

    let cleaned = inputVal.trim();
    const dialCode = countries.find((c) => c.code === cCode)?.dialCode || '';
    if (cleaned.startsWith(dialCode)) {
      cleaned = cleaned.substring(dialCode.length).trim();
    }

    try {
      const parsedNum = parsePhoneNumberFromString(cleaned, cCode);
      if (parsedNum?.isValid()) {
        setValue(`phones.${index}.phone`, parsedNum.number);
        setValue(`phones.${index}.phoneRaw`, inputVal);
        setPhoneInput(parsedNum.formatNational());
        setErrorMsg('');
      } else {
        setErrorMsg('Invalid for selected country');
        setValue(`phones.${index}.phone`, cleaned);
        setValue(`phones.${index}.phoneRaw`, inputVal);
      }
    } catch {
      setErrorMsg('Error parsing phone');
    }
  };

  return (
    <div className="flex flex-col gap-3 bg-slate-50/50 p-3 rounded-xl border border-border/50">
      {/* Top Row: Full Width Phone Input */}
      <div className="w-full">
        <div className="flex items-center bg-white dark:bg-slate-900 border border-input rounded-md overflow-hidden focus-within:ring-2 focus-within:ring-ring focus-within:border-primary">
          <Select
            value={country}
            onValueChange={(val) => {
              setCountry(val as CountryCode);
              validateAndFormat(phoneInput, val as CountryCode);
            }}
          >
            <SelectTrigger className="w-[70px] border-0 h-8 focus:ring-0 focus:ring-offset-0 px-2 shrink-0 select-none shadow-none bg-transparent">
              <span className="text-base">
                {countries.find((c) => c.code === country)?.flag || '🇺🇸'}
              </span>
            </SelectTrigger>
            <SelectContent className="max-h-[300px]">
              {countries.map((c) => (
                <SelectItem key={c.code} value={c.code}>
                  <span className="mr-2 text-base">{c.flag}</span>
                  <span className="text-xs font-medium">
                    {c.name} ({c.dialCode})
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="w-px h-5 bg-border shrink-0" />

          <span className="text-[11px] text-muted-foreground font-semibold px-2 shrink-0 select-none">
            {countries.find((c) => c.code === country)?.dialCode || '+1'}
          </span>

          <Input
            type="tel"
            value={phoneInput}
            onChange={(e) => {
              setPhoneInput(e.target.value);
              setErrorMsg('');
            }}
            onBlur={() => validateAndFormat(phoneInput, country)}
            placeholder="555-0199"
            className="flex-1 border-0 h-8 text-xs focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent py-1 pl-1 pr-3 shadow-none"
          />
        </div>
        {errorMsg && (
          <span className="text-[10px] text-destructive font-semibold block mt-1 px-1">
            {errorMsg}
          </span>
        )}
      </div>

      {/* Bottom Row: Controls */}
      <div className="flex items-center justify-between gap-3 pt-1 border-t border-dashed border-border/60">
        {/* Label Selector */}
        <div className="w-28 shrink-0">
          <Controller
            name={`phones.${index}.label`}
            control={control}
            render={({ field }) => (
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="h-8 text-[11px] bg-white dark:bg-slate-900 shadow-none">
                  <SelectValue placeholder="Label" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mobile">Mobile</SelectItem>
                  <SelectItem value="work">Work</SelectItem>
                  <SelectItem value="home">Home</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </div>

        {/* Action Controls Group */}
        <div className="flex items-center gap-3">
          {/* Main Primary Checkbox */}
          <div className="flex items-center gap-1.5 select-none">
            <Controller
              name={`phones.${index}.isPrimary`}
              control={control}
              render={({ field }) => (
                <Checkbox
                  id={`phone-primary-${index}`}
                  checked={field.value}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      phoneFields.forEach((_, idx) => {
                        if (idx !== index) {
                          setValue(`phones.${idx}.isPrimary`, false);
                        }
                      });
                    }
                    field.onChange(checked);
                  }}
                />
              )}
            />
            <label
              htmlFor={`phone-primary-${index}`}
              className="text-[9px] font-bold text-muted-foreground uppercase cursor-pointer tracking-wider"
            >
              Main Number
            </label>
          </div>

          <div className="w-px h-4 bg-border" />

          {/* Delete Button */}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/5 shrink-0 rounded-lg"
            onClick={onRemove}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

interface EmailInputRowProps {
  index: number;
  control: Control<ContactEditFormData>;
  setValue: UseFormSetValue<ContactEditFormData>;
  onRemove: () => void;
  emailFields: FieldArrayWithId<ContactEditFormData, 'emails', 'id'>[];
}

function EmailInputRow({ index, control, setValue, onRemove, emailFields }: EmailInputRowProps) {
  const initialValue = control._defaultValues?.emails?.[index]?.email || '';
  const [emailInput, setEmailInput] = useState(initialValue);

  const handleBlur = (val: string) => {
    const formatted = val.trim().toLowerCase();
    setEmailInput(formatted);
    setValue(`emails.${index}.email`, formatted);
  };

  return (
    <div className="flex flex-col gap-3 bg-slate-50/50 p-3 rounded-xl border border-border/50">
      {/* Top Row: Full Width Email Input */}
      <div className="w-full">
        <Controller
          name={`emails.${index}.email`}
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              type="email"
              value={emailInput}
              onChange={(e) => {
                setEmailInput(e.target.value);
                field.onChange(e.target.value);
              }}
              onBlur={(e) => handleBlur(e.target.value)}
              placeholder="email@example.com"
              className="w-full text-xs bg-white dark:bg-slate-900 h-8 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-0 shadow-none"
            />
          )}
        />
      </div>

      {/* Bottom Row: Controls */}
      <div className="flex items-center justify-between gap-3 pt-1 border-t border-dashed border-border/60">
        {/* Label Selector */}
        <div className="w-28 shrink-0">
          <Controller
            name={`emails.${index}.label`}
            control={control}
            render={({ field }) => (
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="h-8 text-[11px] bg-white dark:bg-slate-900 shadow-none">
                  <SelectValue placeholder="Label" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="work">Work</SelectItem>
                  <SelectItem value="personal">Personal</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            )}
          />
        </div>

        {/* Action Controls Group */}
        <div className="flex items-center gap-3">
          {/* Main Primary Checkbox */}
          <div className="flex items-center gap-1.5 select-none">
            <Controller
              name={`emails.${index}.isPrimary`}
              control={control}
              render={({ field }) => (
                <Checkbox
                  id={`email-primary-${index}`}
                  checked={field.value}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      emailFields.forEach((_, idx) => {
                        if (idx !== index) {
                          setValue(`emails.${idx}.isPrimary`, false);
                        }
                      });
                    }
                    field.onChange(checked);
                  }}
                />
              )}
            />
            <label
              htmlFor={`email-primary-${index}`}
              className="text-[9px] font-bold text-muted-foreground uppercase cursor-pointer tracking-wider"
            >
              Main Email
            </label>
          </div>

          <div className="w-px h-4 bg-border" />

          {/* Delete Button */}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-destructive hover:bg-destructive/5 shrink-0 rounded-lg animate-fade-in"
            onClick={onRemove}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

type ContactDetail = NonNullable<RouterOutputs['contacts']['getById']>;

export interface ContactEditFormProps {
  /** The contact being edited. */
  contact: ContactDetail;
  isSubmitting?: boolean;
  onDelete?: () => void;
  isDeleting?: boolean;
  onCancel: () => void;
  onSubmit: (data: ContactEditFormData) => void;
  isReadOnly?: boolean;
  /** Optional prefill chips (e.g. participants extracted from an email thread). */
  suggestedParticipants?: Array<{ name: string; email?: string; type: 'sender' | 'stakeholder' }>;
}

/** Maps a Contact record onto the flat form value shape (excluding custom fields). */
function toFormValues(contact: ContactDetail, fallbackOwnerId?: number) {
  return {
    label: contact.label ?? '',
    content: contact.content ?? '',
    typeId: contact.typeId ?? null,
    displayName: contact.displayName ?? '',
    avatarUrl: contact.avatarUrl ?? null,
    prefix: contact.prefix ?? '',
    firstName: contact.firstName ?? '',
    middleName: contact.middleName ?? '',
    lastName: contact.lastName ?? '',
    suffix: contact.suffix ?? '',
    jobTitle: contact.jobTitle ?? '',
    companyName: contact.companyName ?? '',
    website: contact.website ?? '',
    ownerId: contact.ownerId ?? fallbackOwnerId ?? undefined,
    addressStreet: contact.addressStreet ?? '',
    addressCity: contact.addressCity ?? '',
    addressState: contact.addressState ?? '',
    addressPostalCode: contact.addressPostalCode ?? '',
    addressCountry: contact.addressCountry ?? '',
    notes: sanitizeRichText(contact.notes ?? ''),
    emails: (contact.emails ?? []).map((e) => ({
      email: e.email,
      label: e.label ?? 'work',
      isPrimary: e.isPrimary ?? false,
    })),
    phones: (contact.phones ?? []).map((p) => ({
      phone: p.phone,
      phoneRaw: p.phoneRaw ?? undefined,
      label: p.label ?? 'work',
      isPrimary: p.isPrimary ?? false,
    })),
  };
}

/**
 * The full contact profile editor — every core field plus per-type custom
 * (meta) fields. Shared by both portals (clinic + anchor). Router-agnostic:
 * the caller owns navigation and the create/update mutation via `onSubmit`.
 */
export function ContactEditForm({
  contact,
  isSubmitting = false,
  onDelete,
  isDeleting = false,
  onCancel,
  onSubmit,
  isReadOnly = false,
  suggestedParticipants,
}: ContactEditFormProps) {
  const { toastError } = useToast();
  const isInitializedRef = useRef(false);
  // Tracks the contact id whose custom-field values have been merged into the
  // form, so we only overwrite them once per contact (never over live edits).
  const customFieldsAppliedRef = useRef<number | null>(null);

  const { uploadAll } = useFileUpload();

  const { data: contactTags = [] } = trpc.tags.getForContact.useQuery(
    { contactId: contact?.id ?? 0 },
    { enabled: !!contact?.id }
  );
  const { data: allTagsData } = trpc.tags.list.useQuery();
  const allTags = allTagsData?.items || [];
  const { data: members = [] } = trpc.workspace.listMembers.useQuery();
  const meQuery = trpc.auth.me.useQuery();

  const utils = trpc.useUtils();
  const [tagInput, setTagInput] = useState('');

  const upsertTagMutation = trpc.tags.upsert.useMutation();
  const associateTagMutation = trpc.tags.associateWithContact.useMutation({
    onSuccess: () => utils.tags.getForContact.invalidate({ contactId: contact?.id ?? 0 }),
  });
  const dissociateTagMutation = trpc.tags.dissociateFromContact.useMutation({
    onSuccess: () => utils.tags.getForContact.invalidate({ contactId: contact?.id ?? 0 }),
  });

  const handleAddTag = async () => {
    const name = tagInput.trim();
    if (!name || !contact?.id) return;
    setTagInput('');
    const existing = allTags.find((t) => t.label.toLowerCase() === name.toLowerCase());
    const tag = existing ?? (await upsertTagMutation.mutateAsync({ name }));
    await associateTagMutation.mutateAsync({ tagId: tag.id, contactId: contact.id });
    utils.tags.list.invalidate();
  };

  const handleRemoveTag = (associationId: number) => {
    dissociateTagMutation.mutate({ id: associationId });
  };

  const { data: contactTypesData, isLoading: isLoadingTypes } = trpc.contactTypes.list.useQuery();
  const contactTypes = contactTypesData?.items || [];

  const { control, handleSubmit, reset, setValue } = useForm<ContactEditFormData>({
    // zodResolver's inferred Resolver<T> and react-hook-form's Resolver<T> structurally match but
    // TS's comparison bottoms out on this schema's size (~35 fields incl. 20 filterSlot unions),
    // reporting them as "unrelated" — bridge through unknown rather than widening to `any`.
    resolver: zodResolver(contactEditSchema) as unknown as Resolver<ContactEditFormData>,
    defaultValues: toFormValues(contact, meQuery.data?.uid),
    mode: 'onChange',
  });
  const contactAddress: AddressEditorValue = {
    street: useWatch({ control, name: 'addressStreet' }) ?? '',
    locality: useWatch({ control, name: 'addressCity' }) ?? '',
    region: useWatch({ control, name: 'addressState' }) ?? '',
    postalCode: useWatch({ control, name: 'addressPostalCode' }) ?? '',
    country: useWatch({ control, name: 'addressCountry' }) ?? '',
    countryCode: '',
    formattedAddress: '',
    googlePlaceId: null,
    latitude: null,
    longitude: null,
  };
  contactAddress.formattedAddress = formatAddress(contactAddress);

  // Contact avatar upload. Mirrors ProfileForm's flow (pick → crop → presigned
  // PUT), but the uploaded URL is only staged into the form — it isn't persisted
  // until the form is submitted, so cancelling an edit discards it like any
  // other field.
  const getAvatarUploadUrlMutation = trpc.contacts.getAvatarUploadUrl.useMutation();
  const [isUploadingAvatar, setIsUploadingAvatar] = useState(false);
  const [selectedFileForCrop, setSelectedFileForCrop] = useState<File | null>(null);
  const [isCropperOpen, setIsCropperOpen] = useState(false);
  const avatarFileInputRef = useRef<HTMLInputElement>(null);

  const handleAvatarFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    // Always clear the input, so re-picking the same file fires change again.
    if (avatarFileInputRef.current) avatarFileInputRef.current.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toastError('Please select a valid image file');
      return;
    }
    if (file.size > MAX_AVATAR_BYTES) {
      toastError('Image size must be less than 5MB');
      return;
    }

    setSelectedFileForCrop(file);
    setIsCropperOpen(true);
  };

  const handleAvatarCropComplete = async (croppedBlob: Blob) => {
    setIsUploadingAvatar(true);
    try {
      const fileName = selectedFileForCrop
        ? `contact_${selectedFileForCrop.name}`
        : 'contact_avatar.png';
      // The cropper always hands back a PNG regardless of the source format.
      const fileType = 'image/png';

      const { uploadUrl, viewUrl } = await getAvatarUploadUrlMutation.mutateAsync({
        fileName,
        fileType,
      });

      const uploadRes = await fetch(uploadUrl, {
        method: 'PUT',
        body: croppedBlob,
        headers: { 'Content-Type': fileType },
      });

      if (!uploadRes.ok) {
        throw new Error('Failed to upload contact photo to storage');
      }

      setValue('avatarUrl', viewUrl, { shouldDirty: true });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Error uploading contact photo';
      toastError(msg);
    } finally {
      setIsUploadingAvatar(false);
      setSelectedFileForCrop(null);
    }
  };

  const {
    fields: emailFields,
    append: appendEmail,
    remove: removeEmail,
  } = useFieldArray({ control, name: 'emails' });

  const {
    fields: phoneFields,
    append: appendPhone,
    remove: removePhone,
  } = useFieldArray({ control, name: 'phones' });

  const allValues = useWatch({ control });
  const selectedTypeId = allValues.typeId;

  const selectedType = React.useMemo(() => {
    return contactTypes.find((t) => t.id === selectedTypeId);
  }, [contactTypes, selectedTypeId]);

  const customFields = React.useMemo(() => {
    return mapSchemaToFields(selectedType?.customFieldsSchema);
  }, [selectedType]);

  // Reset the flat (non-custom) fields whenever we switch to a different contact.
  // Keyed on contact?.id only (not the whole object / me query) so a background
  // refetch of the same contact never clobbers the user's in-progress edits.
  // biome-ignore lint/correctness/useExhaustiveDependencies: intentional — see above
  useEffect(() => {
    reset(toFormValues(contact, meQuery.data?.uid));
    isInitializedRef.current = true;
    customFieldsAppliedRef.current = null;
  }, [contact?.id, reset]);

  // Merge existing custom-field values (stored in contact.content JSON) into the
  // form once the contact's type — and therefore its field schema — is known.
  // Runs once per contact so it never overwrites the user's live edits.
  useEffect(() => {
    if (!contact || customFields.length === 0) return;
    if (customFieldsAppliedRef.current === contact.id) return;
    const defaults = getCustomFieldDefaults(customFields, contact as Record<string, unknown>);
    Object.entries(defaults).forEach(([key, value]) => {
      setValue(
        key as keyof ContactEditFormData,
        value as ContactEditFormData[keyof ContactEditFormData]
      );
    });
    customFieldsAppliedRef.current = contact.id;
  }, [contact, customFields, setValue]);

  // Keep `content` (JSON blob) and filter slots in sync with the custom-field
  // inputs. Held back until the initial custom-field values have been applied so
  // it never flushes an empty blob over real data during first load.
  useEffect(() => {
    if (!isInitializedRef.current) return;
    if (customFields.length > 0 && customFieldsAppliedRef.current !== contact?.id) return;

    const submission = prepareContactSubmission(customFields, allValues);
    Object.entries(submission).forEach(([key, value]) => {
      const currentValues = allValues as Record<string, unknown>;
      if (currentValues[key] !== value) {
        setValue(
          key as keyof ContactEditFormData,
          value as ContactEditFormData[keyof ContactEditFormData]
        );
      }
    });

    const fileIds = extractFileAssociations(customFields, allValues as Record<string, unknown>);
    const associations = fileIds.map((id) => ({ fileId: id, category: 'custom-field' }));
    if (JSON.stringify(associations) !== JSON.stringify(allValues.fileAssociations)) {
      setValue('fileAssociations', associations);
    }
  }, [allValues, setValue, customFields, contact?.id]);

  const handleUploadFile = async (file: File, category?: string) => {
    const ids = await uploadAll([file], category);
    if (ids.length === 0) throw new Error('Upload failed');
    return ids[0];
  };

  const handleGetFile = async (fileId: number) => {
    const file = await trpcClient.files.getById.query({ id: fileId });
    if (!file) throw new Error('File not found');
    let viewUrl = file.publicUrl || undefined;
    if (!viewUrl) {
      viewUrl = await trpcClient.files.getViewUrl.query({ id: fileId });
    }
    return {
      fileName: file.fileName,
      fileType: file.fileType,
      viewUrl,
    };
  };

  const handleRenameFile = async (fileId: number, newName: string) => {
    await trpcClient.files.update.mutate({ id: fileId, label: newName });
  };

  const onFormSubmit = (data: ContactEditFormData) => {
    onSubmit({
      ...data,
      content: data.content || '{}',
    });
  };

  if (!contact) return null;

  return (
    <>
      <ImageCropperModal
        isOpen={isCropperOpen}
        imageFile={selectedFileForCrop}
        title="Crop Contact Photo"
        onClose={() => {
          setIsCropperOpen(false);
          setSelectedFileForCrop(null);
        }}
        onCropComplete={handleAvatarCropComplete}
      />
      <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
        {suggestedParticipants && suggestedParticipants.length > 0 && (
          <div className="space-y-2 p-4 bg-slate-50/50 dark:bg-slate-900/50 border border-dashed rounded-xl">
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground block">
              Prefill from Thread Participants:
            </span>
            <div className="flex flex-wrap gap-2 mt-2">
              {suggestedParticipants.map((p, idx) => (
                <button
                  key={idx}
                  type="button"
                  className="px-2.5 py-1 text-xs font-semibold rounded-full bg-white dark:bg-slate-800 border hover:border-primary/50 text-slate-700 dark:text-slate-200 transition-all flex items-center gap-1.5 shadow-2xs cursor-pointer select-none hover:bg-slate-50/50"
                  onClick={() => {
                    setValue('displayName', p.name);
                    setValue('label', p.name);
                    const nameParts = p.name.split(/\s+/);
                    if (nameParts.length >= 2) {
                      setValue('firstName', nameParts[0]);
                      setValue('lastName', nameParts.slice(1).join(' '));
                    } else {
                      setValue('firstName', p.name);
                      setValue('lastName', '');
                    }
                    if (p.email) {
                      setValue('emails', [{ email: p.email, label: 'work', isPrimary: true }]);
                    } else {
                      setValue('emails', []);
                    }
                  }}
                >
                  <User className="h-3 w-3 text-blue-500" />
                  <span>{p.name}</span>
                  {p.email && (
                    <span className="text-[10px] text-muted-foreground">({p.email})</span>
                  )}
                  <span className="text-[9px] bg-slate-100 dark:bg-slate-700 px-1.5 py-0.25 rounded-full text-slate-500 dark:text-slate-350 capitalize font-medium">
                    {p.type}
                  </span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Section 1: Core Identity */}
        <div className="space-y-4">
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
            <User className="h-3.5 w-3.5" /> Core Identity
          </h4>
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <Controller
                name="avatarUrl"
                control={control}
                render={({ field }) => (
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <UserAvatar
                        name={allValues.displayName || contact.label}
                        avatarUrl={field.value}
                        size="xl"
                        showTooltip={false}
                        className="h-16 w-16 border border-border"
                      />
                      {isUploadingAvatar && (
                        <div className="absolute inset-0 flex items-center justify-center rounded-full bg-background/80 backdrop-blur-xs">
                          <Loader2 className="h-5 w-5 animate-spin text-primary" />
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col gap-1.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <input
                          type="file"
                          ref={avatarFileInputRef}
                          onChange={handleAvatarFileSelect}
                          accept="image/png,image/jpeg,image/webp,image/jpg"
                          className="hidden"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          disabled={isUploadingAvatar || isReadOnly}
                          onClick={() => avatarFileInputRef.current?.click()}
                          className="gap-2"
                        >
                          <Camera className="h-3.5 w-3.5" />
                          {field.value ? 'Change Photo' : 'Upload Photo'}
                        </Button>

                        {field.value && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            disabled={isUploadingAvatar || isReadOnly}
                            onClick={() => field.onChange(null)}
                            className="gap-1.5 text-destructive hover:bg-destructive/10 hover:text-destructive"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                            Remove
                          </Button>
                        )}
                      </div>
                      <p className="text-[11px] text-muted-foreground">
                        PNG, JPG, or WebP. Max 5MB. Saved when you save the contact.
                      </p>
                    </div>
                  </div>
                )}
              />
            </div>

            <div className="space-y-2 col-span-2">
              <Label htmlFor="contact-displayName" className="text-xs font-semibold">
                Display Name
              </Label>
              <Controller
                name="displayName"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="contact-displayName"
                    placeholder="e.g. John Doe / Acme Corp"
                    className="w-full text-sm bg-white dark:bg-slate-900"
                    value={field.value || ''}
                    disabled={isReadOnly}
                  />
                )}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contact-label" className="text-xs font-semibold">
                Internal Identifier (Label)
              </Label>
              <Controller
                name="label"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="contact-label"
                    placeholder="Internal lookup name"
                    className="w-full text-sm bg-white dark:bg-slate-900"
                    disabled={isReadOnly}
                    required
                  />
                )}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contact-type" className="text-xs font-semibold">
                Contact Type <span className="font-normal text-muted-foreground">(optional)</span>
              </Label>
              <Controller
                name="typeId"
                control={control}
                render={({ field }) => (
                  <Select
                    onValueChange={(val) =>
                      field.onChange(val === NO_TYPE_VALUE ? null : Number(val))
                    }
                    value={field.value?.toString() ?? NO_TYPE_VALUE}
                    disabled={isLoadingTypes || isReadOnly}
                  >
                    <SelectTrigger
                      id="contact-type"
                      className="w-full bg-white dark:bg-slate-900 text-sm"
                    >
                      <SelectValue placeholder={isLoadingTypes ? 'Loading...' : 'No type'} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_TYPE_VALUE}>No type</SelectItem>
                      {contactTypes.map((type) => (
                        <SelectItem key={type.id} value={type.id.toString()}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="contact-owner" className="text-xs font-semibold">
                Owner
              </Label>
              <Controller
                name="ownerId"
                control={control}
                render={({ field }) => (
                  <Select
                    onValueChange={(val) => field.onChange(Number(val))}
                    value={field.value?.toString()}
                    disabled={isReadOnly}
                  >
                    <SelectTrigger
                      id="contact-owner"
                      className="w-full bg-white dark:bg-slate-900 text-sm"
                    >
                      <SelectValue placeholder="Select an owner..." />
                    </SelectTrigger>
                    <SelectContent>
                      {members.map((member) => (
                        <SelectItem key={member.id} value={member.id.toString()}>
                          {member.fullName || member.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              />
            </div>
          </div>
        </div>

        {/* Section 2: Name Breakdown */}
        <div className="space-y-4 pt-4 border-t">
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
            <User className="h-3.5 w-3.5 text-blue-500" /> Individual Name Breakdown
          </h4>
          <div className="grid grid-cols-12 gap-3">
            <div className="space-y-2 col-span-3">
              <Label htmlFor="contact-prefix" className="text-[10px] font-semibold">
                Prefix
              </Label>
              <Controller
                name="prefix"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="contact-prefix"
                    placeholder="Mr./Dr."
                    className="text-xs bg-white dark:bg-slate-900 h-9"
                    value={field.value || ''}
                    disabled={isReadOnly}
                  />
                )}
              />
            </div>
            <div className="space-y-2 col-span-9">
              <Label htmlFor="contact-firstName" className="text-[10px] font-semibold">
                First Name
              </Label>
              <Controller
                name="firstName"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="contact-firstName"
                    placeholder="First Name"
                    className="text-xs bg-white dark:bg-slate-900 h-9"
                    value={field.value || ''}
                    disabled={isReadOnly}
                  />
                )}
              />
            </div>
            <div className="space-y-2 col-span-4">
              <Label htmlFor="contact-middleName" className="text-[10px] font-semibold">
                Middle Name
              </Label>
              <Controller
                name="middleName"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="contact-middleName"
                    placeholder="Middle Name"
                    className="text-xs bg-white dark:bg-slate-900 h-9"
                    value={field.value || ''}
                    disabled={isReadOnly}
                  />
                )}
              />
            </div>
            <div className="space-y-2 col-span-5">
              <Label htmlFor="contact-lastName" className="text-[10px] font-semibold">
                Last Name
              </Label>
              <Controller
                name="lastName"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="contact-lastName"
                    placeholder="Last Name"
                    className="text-xs bg-white dark:bg-slate-900 h-9"
                    value={field.value || ''}
                    disabled={isReadOnly}
                  />
                )}
              />
            </div>
            <div className="space-y-2 col-span-3">
              <Label htmlFor="contact-suffix" className="text-[10px] font-semibold">
                Suffix
              </Label>
              <Controller
                name="suffix"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="contact-suffix"
                    placeholder="Jr./III"
                    className="text-xs bg-white dark:bg-slate-900 h-9"
                    value={field.value || ''}
                    disabled={isReadOnly}
                  />
                )}
              />
            </div>
          </div>
        </div>

        {/* Section 3: Professional Info */}
        <div className="space-y-4 pt-4 border-t">
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
            <Briefcase className="h-3.5 w-3.5 text-blue-500" /> Corporate / Workspace Profile
          </h4>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="contact-jobTitle" className="text-xs font-semibold">
                Job Title
              </Label>
              <Controller
                name="jobTitle"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="contact-jobTitle"
                    placeholder="e.g. Account Executive"
                    className="text-sm bg-white dark:bg-slate-900"
                    value={field.value || ''}
                    disabled={isReadOnly}
                  />
                )}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact-companyName" className="text-xs font-semibold">
                Company Name
              </Label>
              <Controller
                name="companyName"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="contact-companyName"
                    placeholder="e.g. Stripe"
                    className="text-sm bg-white dark:bg-slate-900"
                    value={field.value || ''}
                    disabled={isReadOnly}
                  />
                )}
              />
            </div>
            <div className="space-y-2 col-span-2">
              <Label htmlFor="contact-website" className="text-xs font-semibold">
                Corporate Website URL
              </Label>
              <Controller
                name="website"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    id="contact-website"
                    placeholder="e.g. stripe.com"
                    className="text-sm bg-white dark:bg-slate-900"
                    value={field.value || ''}
                    disabled={isReadOnly}
                  />
                )}
              />
            </div>
          </div>
        </div>

        {/* Section 3.5: Mailing Address */}
        <div className="space-y-4 border-t pt-4">
          <h4 className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-slate-400">
            <MapPin className="h-3.5 w-3.5 text-blue-500" /> Mailing Address
          </h4>
          <AddressEditor
            disabled={isReadOnly}
            idPrefix="contact-address"
            onChange={(address) => {
              setValue('addressStreet', address.street, { shouldDirty: true });
              setValue('addressCity', address.locality, { shouldDirty: true });
              setValue('addressState', address.region, { shouldDirty: true });
              setValue('addressPostalCode', address.postalCode, { shouldDirty: true });
              setValue('addressCountry', address.country, { shouldDirty: true });
            }}
            searchLabel="Find mailing address"
            value={contactAddress}
          />
        </div>

        {/* Section 3.6: Notes */}
        <div className="space-y-4 pt-4 border-t">
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
            <StickyNote className="h-3.5 w-3.5 text-blue-500" /> Notes
          </h4>
          <Controller
            name="notes"
            control={control}
            render={({ field }) => (
              <TiptapEditor
                value={field.value || ''}
                onChange={field.onChange}
                placeholder="Write anything worth remembering about this contact..."
                minRows={10}
                maxRows={40}
                users={members}
              />
            )}
          />
        </div>

        {/* Section 3.7: Tags (existing contacts only) */}
        {contact?.id && (
          <div className="space-y-4 pt-4 border-t">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
              <TagIcon className="h-3.5 w-3.5 text-blue-500" /> Tags
            </h4>
            <TagChips
              tags={contactTags}
              onRemove={
                isReadOnly
                  ? undefined
                  : (tag) => {
                      const match = contactTags.find((t) => t.id === tag.id);
                      if (match) handleRemoveTag(match.associationId);
                    }
              }
            />
            {!isReadOnly && (
              <div className="flex gap-2">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                  placeholder="Add a tag and press Enter..."
                  list="contact-existing-tags"
                  className="text-sm bg-white dark:bg-slate-900"
                />
                <datalist id="contact-existing-tags">
                  {allTags.map((t) => (
                    <option key={t.id} value={t.label} />
                  ))}
                </datalist>
                <Button type="button" variant="outline" onClick={handleAddTag}>
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Section 4: Emails */}
        <div className="space-y-4 pt-4 border-t">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
              <Mail className="h-3.5 w-3.5 text-blue-500" /> Email Addresses
            </h4>
            {!isReadOnly && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 text-xs gap-1"
                onClick={() =>
                  appendEmail({ email: '', label: 'work', isPrimary: emailFields.length === 0 })
                }
              >
                <Plus className="h-3.5 w-3.5" /> Add Email
              </Button>
            )}
          </div>
          <div className="space-y-3">
            {emailFields.map((field, index) => (
              <EmailInputRow
                key={field.id}
                index={index}
                control={control}
                setValue={setValue}
                onRemove={() => removeEmail(index)}
                emailFields={emailFields}
              />
            ))}
            {emailFields.length === 0 && (
              <p className="text-[11px] text-muted-foreground italic text-center py-2">
                No emails registered. Click &quot;Add Email&quot; above.
              </p>
            )}
          </div>
        </div>

        {/* Section 5: Phones */}
        <div className="space-y-4 pt-4 border-t">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
              <Phone className="h-3.5 w-3.5 text-blue-500" /> Phone Numbers
            </h4>
            {!isReadOnly && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 text-xs gap-1"
                onClick={() =>
                  appendPhone({
                    phone: '',
                    phoneRaw: '',
                    label: 'mobile',
                    isPrimary: phoneFields.length === 0,
                  })
                }
              >
                <Plus className="h-3.5 w-3.5" /> Add Phone
              </Button>
            )}
          </div>
          <div className="space-y-3">
            {phoneFields.map((field, index) => (
              <PhoneInputRow
                key={field.id}
                index={index}
                control={control}
                setValue={setValue}
                onRemove={() => removePhone(index)}
                phoneFields={phoneFields}
              />
            ))}
            {phoneFields.length === 0 && (
              <p className="text-[11px] text-muted-foreground italic text-center py-2">
                No phones registered. Click &quot;Add Phone&quot; above.
              </p>
            )}
          </div>
        </div>

        {/* Custom (meta) fields — the per-type schema, same renderer as the extension */}
        {selectedTypeId && customFields.length > 0 && (
          <div className="space-y-4 p-4 rounded-xl bg-slate-50/50 dark:bg-slate-900/50 border border-border/50">
            <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              {selectedType?.label} Attributes
            </p>
            <FormRenderer
              fields={customFields}
              mode={isReadOnly ? 'view' : 'edit'}
              control={control as unknown as Control<FieldValues>}
              onUploadFile={handleUploadFile}
              onGetFile={handleGetFile}
              onRenameFile={handleRenameFile}
            />
          </div>
        )}
        {selectedTypeId && customFields.length === 0 && !isLoadingTypes && (
          <div className="p-3 rounded-xl bg-muted/30 border border-dashed border-border/50 text-center">
            <p className="text-xs text-muted-foreground italic">
              No custom fields defined for {selectedType?.label || 'this type'}.
            </p>
          </div>
        )}

        {!isReadOnly && (
          <div className="pt-6 border-t flex flex-col gap-3">
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" className="flex-1 gap-1.5" disabled={isSubmitting}>
                {isSubmitting ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Save className="h-4 w-4" />
                )}
                {isSubmitting
                  ? contact?.id
                    ? 'Saving...'
                    : 'Creating...'
                  : contact?.id
                    ? 'Save Changes'
                    : 'Create Contact'}
              </Button>
            </div>
            {onDelete && (
              <Button
                type="button"
                variant="destructive"
                onClick={onDelete}
                disabled={isSubmitting || isDeleting}
                className="w-full gap-1.5"
              >
                <Trash className="h-3.5 w-3.5" />
                {isDeleting ? 'Deleting...' : 'Delete Contact'}
              </Button>
            )}
          </div>
        )}
      </form>
    </>
  );
}
